import React from 'react';
import _isString from 'lodash/isString';
import _merge from 'lodash/merge';
import TooltipTrigger, { TooltipTriggerProps } from 'react-popper-tooltip';
import { getContext, isAnimationDisabled } from 'common';
import Elevation, { ElevationProps } from 'components/Elevation';
import Styled from './styles';
import TooltipAnimation from './Animated.TooltipContainer';

const ELEVATION_INDEX = 2;

export interface TooltipProps extends React.ComponentPropsWithoutRef<'div'>, Partial<Omit<TooltipTriggerProps, 'children' | 'tooltip'>> {
  /** Trigger content of the tooltip. This is the content that the `Tooltip` component will wrap. See examples for demonstration. */
  children: React.ReactNode;
  /** The content of the tooltip. */
  tooltip: React.ReactNode;
  /** Disables tooltip from triggering (renders children only) */
  disable?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Control animation config here. This uses react-spring transitions config. (useSpring hook) See https://react-spring.io for more information. */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** Override the Elevation properties. See Elevation component for details. */
  elevationProps?: Partial<ElevationProps>;
  /** Forward props to the container div that wraps the tooltip content. */
  containerProps?: {};
  /** @ignore, it is used internally by Tab */
  triggerRef?: React.RefObject<HTMLElement> | React.ForwardedRef<HTMLElement>;
}

/** Tooltips display informative text when a user hovers over of focuses on an interactive elements in the GUI. All additional props are forwarded to the `react-popper-tooltip` component. See [react-popper-tooltip](https://github.com/mohsinulhaq/react-popper-tooltip/tree/v2.11.1) documentation for more information on the api (this includes adding delay, adjusting placement etc.).   */
const Tooltip = React.forwardRef<HTMLElement, TooltipProps>((props: TooltipProps, ref) =>
  getContext(({ RTL, theme, disableAnimation }) => {
    const {
      children,
      tooltip,
      disable,
      theme: propTheme,
      animationProps,
      disableAnimation: propsdisableAnimation,
      elevationProps,
      containerProps,
      ...otherProps
    } = props;
    const resolvedTheme = _merge({}, theme.TOOLTIP, propTheme);
    /* escape hatch */
    if (disable) return children;
    const Content = (contentProps) => {
      const {
        tooltipRef,
        getTooltipProps,
        content
      } = contentProps;
      const resolvedContainerProps = {
        $theme: resolvedTheme,
        isText: _isString(content),
        ...containerProps
      };
      const resolvedTextContainer = <Styled.TooltipTextContainer $rtl={RTL} {...resolvedContainerProps}>{tooltip}</Styled.TooltipTextContainer>;
      const resolvedRichContainer = (
        <Elevation elevationIndex={ELEVATION_INDEX} {...elevationProps}>
          <Styled.TooltipTextContainer $rtl={RTL} {...resolvedContainerProps}>{tooltip}</Styled.TooltipTextContainer>
        </Elevation>
      );
      const resolvedAnimationProps = _merge({}, animationProps, { immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation) });
      const resolvedContent = !(resolvedContainerProps.isText)
        ? <TooltipAnimation animationProps={resolvedAnimationProps}>{resolvedRichContainer}</TooltipAnimation>
        : resolvedTextContainer;

      return <Styled.ContentContainer {...getTooltipProps({ ref: tooltipRef })}>{resolvedContent}</Styled.ContentContainer>;
    };

    return (
      <TooltipTrigger
        delayShow={500}
        delayHide={100}
        {...otherProps}
        tooltip={tooltipProps => <Content ref={ref} {...{ ...tooltipProps, content: tooltip }} />}
      >
        {({ getTriggerProps, triggerRef }) => (
          <span
            {...getTriggerProps({
              ref: triggerRef,
              className: 'trigger',
              style: {
                height: 'fit-content'
              }
            })}
          >
            {children}
          </span>
        )}
      </TooltipTrigger>
    );
  }));

Tooltip.defaultProps = {
  disable: false,
  theme: {},
  animationProps: {},
  disableAnimation: undefined,
  elevationProps: {},
  containerProps: {},
  triggerRef: undefined
};

Tooltip.displayName = 'Tooltip';

export default Tooltip;
